import { Controller } from "@hotwired/stimulus"

// Exit-intent popup for the /newsletter landing page.
//
// Triggers (whichever fires first, after a 5s grace period):
//   - Desktop: mouse leaves the viewport via the top edge (e.clientY < 5)
//   - Mobile:  scrolled past 50% of the page, then scrolled UP > 80px within 1s
//   - Fallback: 45s elapsed without the user interacting with the inline form
//
// Shown at most once per session. Dismissed via close button, ESC, or
// backdrop click; the dismissed state is remembered for the session.
export default class extends Controller {
  static targets = ["popup"]

  static SESSION_KEY = "ph24_newsletter_popup_state" // "shown" | "submitted"
  static GRACE_MS = 5000     // wait this long after page load before arming triggers
  static FALLBACK_MS = 45000  // and show the popup unconditionally after this long

  connect() {
    if (this.alreadyHandled()) return

    this.armed = false
    this.lastScrollY = window.scrollY
    this.lastScrollTime = performance.now()

    this.boundMouseLeave = (e) => this.handleMouseLeave(e)
    this.boundScroll = () => this.handleScroll()
    this.boundKeydown = (e) => this.handleKeydown(e)
    this.boundFormSubmit = (e) => this.handleFormSubmit(e)

    // Listen for newsletter-form submits so we don't pop up after the user
    // has already signed up. Scoped via handleFormSubmit so unrelated forms
    // (navbar search, etc.) don't suppress the popup.
    document.addEventListener("submit", this.boundFormSubmit, true)

    // Arm exit-intent triggers after a grace period (avoid annoying users
    // who haven't even started reading).
    this.armTimer = setTimeout(() => this.arm(), this.constructor.GRACE_MS)

    // Fallback: show after FALLBACK_MS no matter what (still subject to the
    // sessionStorage gate).
    this.fallbackTimer = setTimeout(() => this.show("fallback_timer"), this.constructor.FALLBACK_MS)
  }

  disconnect() {
    clearTimeout(this.armTimer)
    clearTimeout(this.fallbackTimer)
    document.removeEventListener("mouseleave", this.boundMouseLeave)
    document.removeEventListener("scroll", this.boundScroll)
    document.removeEventListener("keydown", this.boundKeydown)
    document.removeEventListener("submit", this.boundFormSubmit, true)
  }

  arm() {
    this.armed = true
    document.addEventListener("mouseleave", this.boundMouseLeave)
    document.addEventListener("scroll", this.boundScroll, { passive: true })
    document.addEventListener("keydown", this.boundKeydown)
  }

  handleMouseLeave(event) {
    // Only fire when the cursor leaves through the TOP of the viewport
    // (toward the URL bar / browser chrome). Side/bottom = not exit intent.
    if (event.clientY < 5) {
      this.show("mouse_leave_top")
    }
  }

  handleScroll() {
    const now = performance.now()
    const y = window.scrollY
    const docHeight = document.documentElement.scrollHeight - window.innerHeight
    if (docHeight <= 0) return

    const scrolledPct = y / docHeight
    const dy = this.lastScrollY - y // positive = scrolling up
    const dt = now - this.lastScrollTime

    // Mobile heuristic: user got at least halfway through, then scrolled up
    // sharply (>80px in <1s) — likely heading back to where they came from.
    if (scrolledPct > 0.5 && dy > 80 && dt < 1000) {
      this.show("scroll_up_after_50pct")
      return
    }

    this.lastScrollY = y
    this.lastScrollTime = now
  }

  handleKeydown(event) {
    if (event.key === "Escape" && this.popupVisible()) {
      this.dismiss()
    }
  }

  // --- show/dismiss ----------------------------------------------------

  show(_reason) {
    if (this.alreadyHandled()) return
    if (!this.hasPopupTarget) return

    this.popupTarget.classList.remove("hidden")
    // Force reflow then add the active class so the transition runs.
    void this.popupTarget.offsetHeight
    this.popupTarget.classList.add("nl-popup-visible")

    document.body.style.overflow = "hidden"
    this.persist("shown")
  }

  dismiss() {
    if (!this.hasPopupTarget) return
    this.popupTarget.classList.remove("nl-popup-visible")
    setTimeout(() => {
      this.popupTarget.classList.add("hidden")
      document.body.style.overflow = ""
    }, 250)
  }

  // Bound to a click on the backdrop (only, not the inner card)
  backdropClick(event) {
    if (event.target === event.currentTarget) {
      this.dismiss()
    }
  }

  markSubmitted() {
    this.persist("submitted")
  }

  // Only treat submits from a newsletter signup form (any element wrapped
  // in a `data-controller="lead-magnet-form"`) as a signup. Any other form
  // on the page (search, etc.) is ignored.
  handleFormSubmit(event) {
    const form = event.target
    if (!form || form.tagName !== "FORM") return
    if (form.closest('[data-controller~="lead-magnet-form"]')) {
      this.markSubmitted()
    }
  }

  // --- session-state helpers ------------------------------------------

  popupVisible() {
    return this.hasPopupTarget && !this.popupTarget.classList.contains("hidden")
  }

  alreadyHandled() {
    try {
      return !!sessionStorage.getItem(this.constructor.SESSION_KEY)
    } catch (_) {
      return false
    }
  }

  persist(state) {
    try {
      sessionStorage.setItem(this.constructor.SESSION_KEY, state)
    } catch (_) { /* ignore */ }
  }
}
